"use client";
import React, { useState, useEffect } from "react";
import { ComponentConfig } from "@/types/models/componentConfig";
import { ColorPicker } from "./ColorPicker";
import { useFileManagerModal } from "@/components/fileManager";
import { FileSystemItem } from "@/types/fileManager";
import { Image as ImageIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { generateImageSrc } from "@/utils/helper";
import { FileManagerService } from "@/services/fileManagerService";

interface DynamicInputProps {
  config: ComponentConfig;
  value: unknown;
  onChange: (value: unknown) => void;
  className?: string;
}

export function DynamicInput({
  config,
  value,
  onChange,
  className = "",
}: DynamicInputProps) {
  const [imageSrc, setImageSrc] = useState<string>("");
  const [loadingAsset, setLoadingAsset] = useState(false);
  const [assetError, setAssetError] = useState<string | null>(null);
  const { openFileManager } = useFileManagerModal();

  // Helper function to check if a string is a UUID
  const isUUID = (str: string): boolean => {
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  };

  // Fetch asset and generate image URL when value changes
  useEffect(() => {
    if (config.type === "image" && typeof value === "string" && value) {
      if (!isUUID(value)) {
        console.warn("❌ Invalid UUID format:", value);
        setAssetError("Invalid asset ID format");
        setImageSrc("");
        setLoadingAsset(false);
        return;
      }

      console.log("🔍 Fetching asset with ID:", value);
      setLoadingAsset(true);
      setAssetError(null);
      setImageSrc("");

      FileManagerService.getAssetById(value)
        .then((response: any) => {
          console.log("🖼️ Asset response:", response);

          // Handle different response structures
          let asset;
          if (response.asset) {
            // New API structure: { asset: {...}, url: "..." }
            asset = response.asset;
          } else if (response.path) {
            // Direct asset structure
            asset = response;
          } else {
            throw new Error("Invalid response structure");
          }

          console.log("🖼️ Asset data:", asset);
          console.log("🖼️ Asset path:", asset.path);

          if (!asset.path) {
            throw new Error("Asset path is missing");
          }

          const cdnUrl = generateImageSrc(asset.path, 300, 200); // Preview size: 300x200
          console.log("🔗 Generated CDN URL:", cdnUrl);
          setImageSrc(cdnUrl);
        })
        .catch((error) => {
          console.error("❌ Failed to fetch asset:", error);
          console.error("❌ Error details:", {
            assetId: value,
            errorMessage: error.message,
            errorStack: error.stack,
          });
          setAssetError(`Failed to load image: ${error.message}`);
          setImageSrc("");
        })
        .finally(() => {
          setLoadingAsset(false);
        });
    } else if (config.type === "image") {
      // Clear state when no value
      
      setImageSrc("");
      setAssetError(null);
      setLoadingAsset(false);
    }
  }, [value, config.type]);

  const baseInputClasses =
    "w-full p-2 text-sm bg-muted dark:bg-auth-background border border-border dark:border-auth-background rounded transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent";

  const handleImageSelect = (files: FileSystemItem[]) => {
    if (files.length > 0) {
      const file = files[0];
      console.log("🖼️ Image selected:", file);
      setAssetError(null);
      onChange(file.assetId);
    }
  };

  const handleOpenFileManager = () => {
    openFileManager({
      selectionMode: "single",
      allowedFileTypes: [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
        "image/svg+xml",
      ],
      maxSelections: 1,
      onSelect: handleImageSelect,
    });
  };

  const handleRemoveImage = () => {
    
    setImageSrc("");
    setAssetError(null);
    onChange("");
  };

  const renderInput = () => {
    switch (config.type) {
      case "text":
      case "default":
        return (
          <input
            type="text"
            value={
              typeof value === "string" ||
              typeof value === "number" ||
              Array.isArray(value)
                ? value
                : ""
            }
            onChange={(e) => onChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            placeholder={config.description}
            className={baseInputClasses}
          />
        );

      case "number":
        return (
          <input
            type="number"
            value={
              typeof value === "string" || typeof value === "number"
                ? value
                : ""
            }
            onChange={(e) => onChange(Number(e.target.value))}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            placeholder={config.description}
            min={config.accepted_range?.[0]}
            max={config.accepted_range?.[1]}
            className={baseInputClasses}
          />
        );

      case "range":
        return (
          <div className="space-y-2">
            <input
              type="range"
              value={
                typeof value === "string" || typeof value === "number"
                  ? value
                  : config.accepted_range?.[0] || 0
              }
              onChange={(e) => onChange(Number(e.target.value))}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              min={config.accepted_range?.[0] || 0}
              max={config.accepted_range?.[1] || 100}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
            />
            <div className="text-xs text-muted-foreground text-center">
              {typeof value === "string" || typeof value === "number"
                ? value
                : config.accepted_range?.[0] || 0}
            </div>
          </div>
        );

      case "select":
        return (
          <select
            value={
              typeof value === "string" || Array.isArray(value) ? value : ""
            }
            onChange={(e) => onChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            className={baseInputClasses}
          >
            <option value="">Select {config.title}</option>
            {config.accepted_values?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case "checkbox":
        return (
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => onChange(e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              className="w-4 h-4 text-primary bg-muted border-border rounded focus:ring-primary focus:ring-2"
            />
            <span className="text-sm text-muted-foreground">
              {config.description}
            </span>
          </div>
        );

      case "color":
        return (
          <ColorPicker
            color={typeof value === "string" ? value : "#000000"}
            onChange={onChange}
          />
        );

      case "image":
        return (
          <div className="space-y-3">
            {loadingAsset && (
              <div className="w-full h-32 bg-muted dark:bg-auth-background border border-border dark:border-auth-background rounded-lg flex items-center justify-center">
                <div className="text-sm text-muted-foreground">
                  Loading image...
                </div>
              </div>
            )}

            {assetError && !loadingAsset && (
              <div className="w-full h-32 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center justify-center">
                <div className="text-sm text-red-600 dark:text-red-400">
                  {assetError}
                </div>
              </div>
            )}

            {!loadingAsset && !assetError && imageSrc && (
              <div className="relative">
                <div className="w-full h-32 bg-muted dark:bg-auth-background border border-border dark:border-auth-background rounded-lg overflow-hidden">
                  <img
                    src={imageSrc}
                    alt="Selected image preview"
                    width={300}
                    height={200}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src =
                        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f3f4f6'/%3E%3Ctext x='50' y='50' font-family='Arial' font-size='12' fill='%236b7280' text-anchor='middle' dy='.3em'%3EImage not found%3C/text%3E%3C/svg%3E";
                    }}
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage();
                  }}
                  className="absolute top-2 right-2 h-6 w-6 p-0 bg-background/80 hover:bg-background"
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            )}

            <Button
              type="button"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenFileManager();
              }}
              className="w-full flex items-center justify-center space-x-2"
              disabled={loadingAsset}
            >
              <ImageIcon className="w-4 h-4" aria-hidden="true" />
              <span>{imageSrc ? "Change Image" : "Select Image"}</span>
            </Button>
          </div>
        );

      case "link":
        return (
          <input
            type="url"
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            placeholder="https://example.com"
            className={baseInputClasses}
          />
        );

      case "date":
        return (
          <input
            type="date"
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            className={baseInputClasses}
          />
        );

      case "richtext":
        return (
          <textarea
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            placeholder={config.description}
            rows={4}
            className={`${baseInputClasses} resize-vertical`}
          />
        );

      case "customcss":
        return (
          <textarea
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            placeholder="/* Enter CSS here */"
            rows={6}
            className={`${baseInputClasses} font-mono text-xs resize-vertical`}
          />
        );

      case "customjs":
        return (
          <textarea
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            placeholder="// Enter JavaScript here"
            rows={6}
            className={`${baseInputClasses} font-mono text-xs resize-vertical`}
          />
        );

      case "customhtml":
        return (
          <textarea
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="<div>Enter HTML here</div>"
            rows={6}
            className={`${baseInputClasses} font-mono text-xs resize-vertical`}
          />
        );

      case "customjson":
        return (
          <textarea
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder='{"key": "value"}'
            rows={6}
            className={`${baseInputClasses} font-mono text-xs resize-vertical`}
          />
        );

      case "multiimage":
        return (
          <div className="space-y-2">
            <div className="text-xs text-muted-foreground">
              Multiple images (comma-separated URLs)
            </div>
            <textarea
              value={
                Array.isArray(value)
                  ? value.join(", ")
                  : typeof value === "string"
                    ? value
                    : ""
              }
              onChange={(e) => {
                const urls = e.target.value
                  .split(",")
                  .map((url) => url.trim())
                  .filter(Boolean);
                onChange(urls);
              }}
              placeholder="https://example.com/image1.jpg, https://example.com/image2.jpg"
              rows={3}
              className={`${baseInputClasses} resize-vertical`}
            />
          </div>
        );

      case "video":
        return (
          <input
            type="url"
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://example.com/video.mp4"
            className={baseInputClasses}
          />
        );

      case "multivideo":
        return (
          <div className="space-y-2">
            <div className="text-xs text-muted-foreground">
              Multiple videos (comma-separated URLs)
            </div>
            <textarea
              value={
                Array.isArray(value)
                  ? value.join(", ")
                  : typeof value === "string"
                    ? value
                    : ""
              }
              onChange={(e) => {
                const urls = e.target.value
                  .split(",")
                  .map((url) => url.trim())
                  .filter(Boolean);
                onChange(urls);
              }}
              placeholder="https://example.com/video1.mp4, https://example.com/video2.mp4"
              rows={3}
              className={`${baseInputClasses} resize-vertical`}
            />
          </div>
        );

      default:
        return (
          <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-sm">
            <span className="text-yellow-800 dark:text-yellow-200">
              Unsupported input type: {config.type}
            </span>
          </div>
        );
    }
  };

  return (
    <div className={`space-y-1 ${className}`}>
      <label className="block text-xs font-medium text-foreground">
        {config.title}
      </label>
      {config.description && (
        <div className="text-xs text-muted-foreground mb-1">
          {config.description}
        </div>
      )}
      {renderInput()}
    </div>
  );
}
